/**
 * Custom @service decorator for extensions with whitelist validation
 * Delegates to Ember's inject() but validates service is whitelisted first
 *
 * Exported as 'inject' because decorator-transforms uses @service -> inject() transform
 *
 * Usage in extensions:
 *   import { service } from '@ember/service';
 *   export default class MyExt {
 *     @service router;           // ✓ if 'router' in whitelist
 *     @service('api') api;       // ✓ if 'api' in whitelist
 *     @service unknown;          // ✗ throws if not whitelisted
 *   }
 *
 * @param name - the service name
 *
 * @returns descriptor from Ember's inject if whitelisted, otherwise throws
 */

import { inject as service } from '@ember/service';
import serviceWhitelist from '../runtime/service-whitelist.ts';

export const inject = (name?: unknown) => {
  // Enforce only for the explicit `@service('name')` form (a string name). The
  // bare `@service` form is applied as a decorator with a non-string argument,
  // so it is delegated to Ember as-is. (An empty whitelist allows everything.)
  if (typeof name === 'string' && !serviceWhitelist.isAllowed(name)) {
    throw new Error(`Service '${name}' is not whitelisted.`);
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return service(name as any);
};
